import { useEffect, useState } from "react";
import { ApiError } from "@/api/client";
import {
  useAttendanceToday,
  useCheckIn,
  useCheckOut,
  useMarkRemote,
  useWithdrawApprovedLeave,
} from "@/api/queries";
import type { AttendanceSession, LeaveDay } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { Skeleton } from "@/components/ui/states";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/cn";
import { formatClock, formatDate, formatTime } from "@/lib/format";
import { leaveLabel } from "@/lib/labels";
import { formatMinutes, HALF_DAY_MINUTES } from "@/lib/org-time";

/** Ticks once a second — drives both the wall clock and the running total. */
function useLiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** How long a still-open session has been running, in ms, as of `now`. */
function elapsedMs(checkIn: string, now: Date): number {
  const start = Date.parse(checkIn);
  if (Number.isNaN(start)) return 0;
  return Math.max(0, now.getTime() - start);
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Today's attendance. Which control appears is decided by the account's work
 * mode, exactly as the backend enforces it:
 *
 *   onsite  Check in, then Check out. One session a day: once it is closed the
 *           day is settled and the button is gone rather than disabled, because
 *           there is no longer an action to offer.
 *
 *   remote  Mark Full Day / Mark Half Day — a DECLARATION, not a clock. One
 *           press settles the day. There is no timer, no second press, and no
 *           hours figure, because nobody is measuring one. It stays changeable
 *           all day, so a morning "full" can become an afternoon "half".
 *
 *   hybrid  Either, picked per day; the first action taken locks the day's mode.
 */
export function CheckInCard() {
  const today = useAttendanceToday();
  const checkIn = useCheckIn();
  const checkOut = useCheckOut();
  const mark = useMarkRemote();
  const withdrawLeave = useWithdrawApprovedLeave();
  const clock = useLiveClock();

  const record = today.data?.record ?? null;
  const workMode = today.data?.workMode;
  const workDate = today.data?.workDate ?? null;
  const sessions = today.data?.sessions ?? [];
  const openSession = today.data?.openSession ?? null;
  const missedCheckOuts = today.data?.missedCheckOuts ?? [];
  // Leave covering the day this card is acting on, if any.
  const leaveToday = today.data?.leave ?? null;
  // An approved work-from-home request covering the day this card is acting on.
  // The card has to say so BEFORE the button is pressed: on an approved day,
  // Check in records a remote day rather than an onsite one, and finding that
  // out afterwards leaves nothing to query it against.
  const wfhToday = today.data?.wfh ?? null;
  const wfhApproved = wfhToday?.status === "approved";
  const busy = checkIn.isPending || checkOut.isPending || mark.isPending;

  const lockedMode = record?.mode ?? null;
  const bankedMinutes = record?.total_minutes ?? 0;

  // A session running right now, opened on an EARLIER day — a genuine overnight
  // shift, or one somebody forgot to close. Either way today is untouched by it.
  const openOnEarlierDay = Boolean(openSession && workDate && openSession.date !== workDate);
  const openToday = Boolean(openSession && workDate && openSession.date === workDate);

  /*
    The running total, live.

    `total_minutes` is what the backend has BANKED — closed sessions only — so
    on its own it sits frozen at the last check-out and a card watched all
    afternoon never moves. That reads as the clock not running at all. The
    session in progress is therefore added here, second by second, off the same
    tick as the wall clock: it counts up while you are checked in and stops dead
    the moment you check out, because there is then no open session to add.

    An overnight session belongs to the day it was opened on, so it is
    deliberately NOT added to today's figure.
  */
  const runningMs = openToday && openSession ? elapsedMs(openSession.check_in, clock) : 0;
  const liveMinutes = bankedMinutes + Math.floor(runningMs / 60_000);
  const runningSeconds = Math.floor(runningMs / 1000) % 60;

  function report(err: unknown) {
    toast.error(err instanceof ApiError ? err.message : "Could not record that.");
  }

  // "Off today" is a one-tap declaration for a day with nothing on it at all —
  // once any session exists the day has been worked and there is nothing to
  // declare.
  const dayIsEmpty = sessions.length === 0 && !record;

  // What today was DECLARED as, for a remote day. 'present'/'half' come straight
  // off the projection; both mean the day is settled and carries no minutes.
  const declared: "full" | "half" | null =
    record?.status === "present" ? "full" : record?.status === "half" ? "half" : null;
  const declaredOff = record?.status === "inactive";

  // The day's one session has been opened and closed: nothing further to press.
  // Deliberately read off the sessions rather than off `record`, because a day
  // HR has corrected to zero minutes still had its session and is still done.
  const dayIsSettled = sessions.length > 0 && sessions.every((session) => session.check_out);

  /*
    Which mode the next press acts in. Once the day has a mode it is settled;
    hybrid before that is the only genuine fork, and it is answered by the small
    Onsite/Remote picker rather than by a second button.
  */
  const [pickedMode, setPickedMode] = useState<"onsite" | "remote">("onsite");
  const dayMode: "onsite" | "remote" =
    lockedMode ??
    openSession?.mode ??
    (workMode === "remote" ? "remote" : workMode === "onsite" ? "onsite" : pickedMode);
  const remoteLabels = dayMode === "remote";
  const isOpen = Boolean(openSession);
  const showModePicker = workMode === "hybrid" && !lockedMode && !isOpen;

  /*
    ONE action button, and it is always the action that actually comes next.

    The card used to show check-in and check-out side by side, on the reasoning
    that a control which comes and goes is a control people stop trusting. In
    practice the pair reads as a question — two filled-looking targets, and the
    only thing separating them is which one is tinted. The next step is never in
    doubt (you are either in or you are out), so the card states it instead of
    asking. Correctness is still the backend's: a press that doesn't make sense
    comes back a plain 409 and nothing is written.
  */
  function primaryAction() {
    if (isOpen) checkOut.mutate(undefined, { onError: report });
    else checkIn.mutate(undefined, { onError: report });
  }

  /** Remote: declare the day, or change what it was declared as. */
  function declare(status: "full" | "half" | "off") {
    mark.mutate({ status }, { onError: report });
  }

  const primaryLabel = isOpen
    ? openOnEarlierDay && openSession
      ? `Check out of ${formatDate(openSession.date, "d MMM")}`
      : "Check out"
    : "Check in";

  return (
    <Panel className="flex min-w-0 flex-col">
      <PanelHeader
        title="Today"
        note={formatDate(workDate) ?? undefined}
        actions={
          <div className="flex shrink-0 items-center gap-2">
            <span className="font-mono text-sm tabular-nums text-muted" aria-label="Current time">
              {formatClock(clock)}
            </span>
            {workMode && (
              <Badge tone="outline">
                {workMode === "onsite" ? "Onsite" : workMode === "remote" ? "Remote" : "Hybrid"}
              </Badge>
            )}
            {wfhApproved && (
              <Badge tone="accent" icon="wfh">
                WFH approved
              </Badge>
            )}
          </div>
        }
      />

      <div className="flex flex-1 flex-col gap-4 p-4">
        {today.isLoading ? (
          <>
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-9 w-40" />
          </>
        ) : (
          <>
            {/* The day's total is the figure that decides it, so it leads —
                except on a declared remote day, which has no hours and must not
                display "0h 00m" as though it had worked none. */}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-mono text-3xl font-medium tabular-nums text-ink">
                {declared ? (declared === "full" ? "Full day" : "Half day") : declaredOff ? "Day off" : (
                  <>
                    {formatMinutes(liveMinutes)}
                    {openToday && <span className="text-xl text-muted">:{pad2(runningSeconds)}</span>}
                  </>
                )}
              </span>
              <span className="flex items-center gap-1.5 text-[13px] text-muted">
                {openToday && (
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-good"
                  />
                )}
                {declared
                  ? `marked at ${formatTime(record?.check_in ?? null) ?? "—"} · no check-out needed`
                  : declaredOff
                    ? "you marked today as not working"
                    : sessions.length === 0
                      ? "nothing recorded yet"
                      : sessions.length === 1
                        ? openToday
                          ? "since you checked in"
                          : "checked in and out"
                        : `across ${sessions.length} sessions`}
              </span>
              {!declared && !declaredOff && sessions.length > 0 &&
                (liveMinutes >= HALF_DAY_MINUTES ? (
                  <Badge tone="good" icon="check-circle">
                    Full day
                  </Badge>
                ) : (
                  <Badge tone="warn" icon="warning-circle">
                    Half day
                  </Badge>
                ))}
            </div>

            {sessions.length > 0 && <SessionList sessions={sessions} now={clock} />}

            {/* The live figure counts the open session; the record doesn't bank
                it until the session closes. Say which is which. */}
            {openToday && (
              <p className="flex items-start gap-1.5 text-[13px] text-muted">
                <Icon name="warning-circle" size={16} className="mt-px shrink-0 text-warn" />
                <span>
                  Counting live. {formatMinutes(bankedMinutes)} is banked so far — the session in
                  progress is added to your record when you check out.
                </span>
              </p>
            )}

            {openOnEarlierDay && openSession && (
              <div className="flex items-start gap-2 rounded-lg border border-line bg-surface-sunken/60 px-3 py-2.5">
                <Icon name="attendance" size={16} className="mt-0.5 shrink-0 text-faint" />
                <p className="text-xs leading-relaxed text-ink-soft">
                  A session opened on{" "}
                  <span className="font-medium text-ink">{formatDate(openSession.date, "d MMM")}</span>{" "}
                  at {formatTime(openSession.check_in)} is still running, so closing it comes first.
                  Checking out settles that day, not today — today starts on the next press.
                </p>
              </div>
            )}

            {missedCheckOuts.length > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-warn/40 bg-warn-wash/40 px-3 py-2.5">
                <Icon name="warning-circle" size={16} className="mt-0.5 shrink-0 text-warn" />
                <p className="text-xs leading-relaxed text-ink-soft">
                  {missedCheckOuts.length === 1 ? "A session" : "Sessions"} on{" "}
                  <span className="font-medium text-ink">
                    {missedCheckOuts.map((day) => formatDate(day.date, "d MMM")).join(", ")}
                  </span>{" "}
                  {missedCheckOuts.length === 1 ? "was" : "were"} never closed, so{" "}
                  {missedCheckOuts.length === 1 ? "that day counts" : "those days count"} as a half
                  day. Ask HR to correct {missedCheckOuts.length === 1 ? "it" : "them"} if you
                  worked longer.
                </p>
              </div>
            )}

            {wfhApproved && workMode === "onsite" && (
              /* Only for ONSITE accounts: they are the ones for whom pressing
                 Check in behaves differently today. A hybrid account already
                 chooses its mode per day and is not overridden, and a remote
                 account cannot have a request at all. */
              <div className="flex items-start gap-2.5 rounded-lg border border-line bg-surface-sunken/60 px-3 py-2.5">
                <Icon name="wfh" size={16} className="mt-0.5 shrink-0 text-muted" />
                <p className="text-[13px] leading-relaxed text-ink-soft">
                  You are approved to work from home today. Check in and check out as usual — the
                  day is recorded as <span className="font-medium text-ink">remote</span> rather
                  than onsite, and is judged on the same hours.
                </p>
              </div>
            )}

            {leaveToday && (
              <LeaveClashNotice
                leave={leaveToday}
                workDate={workDate}
                hasAttendance={sessions.length > 0 || Boolean(declared)}
                busy={withdrawLeave.isPending}
                onWithdraw={() =>
                  withdrawLeave.mutate(
                    { id: leaveToday.request_id, date: leaveToday.date },
                    {
                      onSuccess: () => toast.success("Leave withdrawn for today."),
                      onError: report,
                    },
                  )
                }
              />
            )}

            <div className="mt-auto flex flex-col items-start gap-3">
              {showModePicker && (
                <ModePicker value={pickedMode} onChange={setPickedMode} disabled={busy} />
              )}

              {/* Remote: a declaration, not a clock. Both amounts are offered as
                  equal choices up front — a half day is a normal thing to work,
                  not an exception to be found somewhere else — and once one is
                  taken they stay on screen so it can be changed all day. */}
              {remoteLabels && !openOnEarlierDay ? (
                <div className="flex w-full flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant={declared === "full" ? "primary" : "secondary"}
                      size="lg"
                      className="flex-1 sm:flex-none sm:min-w-40"
                      iconLeft={declared === "full" ? "check-circle" : "check"}
                      loading={busy && mark.variables?.status === "full"}
                      disabled={busy}
                      onClick={() => declare("full")}
                    >
                      Mark Full Day
                    </Button>
                    <Button
                      variant={declared === "half" ? "primary" : "secondary"}
                      size="lg"
                      className="flex-1 sm:flex-none sm:min-w-40"
                      iconLeft={declared === "half" ? "check-circle" : "check"}
                      loading={busy && mark.variables?.status === "half"}
                      disabled={busy}
                      onClick={() => declare("half")}
                    >
                      Mark Half Day
                    </Button>
                  </div>
                  <p className="text-xs leading-relaxed text-muted">
                    {declared
                      ? `Recorded for the ${declared === "half" ? "half" : "whole"} day, from ${
                          formatTime(record?.check_in ?? null) ?? "when you marked it"
                        }. You can change this until the day ends; after that, ask HR.`
                      : "One press records the day, with the time you marked it. No check-out or timer."}
                  </p>
                </div>
              ) : (
                <>
                  {/* A settled day keeps a button ONLY while a session from an
                      earlier day is still open — that press acts on that day, not
                      on this one, so it is still the next thing to do. */}
                  {(!dayIsSettled || openOnEarlierDay) && (
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full sm:w-auto sm:min-w-44"
                      iconLeft={isOpen ? "sign-out" : "check"}
                      loading={busy}
                      onClick={primaryAction}
                    >
                      {primaryLabel}
                    </Button>
                  )}

                  {dayIsSettled && !openOnEarlierDay && (
                    <p className="flex items-start gap-1.5 text-[13px] text-muted">
                      <Icon name="check-circle" size={16} className="mt-px shrink-0 text-good" />
                      <span>Today is recorded and closed. Ask HR if it needs correcting.</span>
                    </p>
                  )}
                </>
              )}

              {/* A remote day off is the same endpoint as "mark inactive", just
                  pressed before anything is recorded. It stays a quiet text
                  action so the card keeps exactly one button. */}
              {canDeclareDayOff(workMode, lockedMode) && dayIsEmpty && !isOpen && !dayIsSettled && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => declare("off")}
                  className="text-[13px] text-muted underline-offset-4 hover:text-ink hover:underline disabled:opacity-50"
                >
                  Mark Day Off — not working today
                </button>
              )}
            </div>

            {declaredOff && sessions.length === 0 && (
              <p className="flex items-center gap-1.5 text-[13px] text-muted">
                <Icon name="x-circle" size={16} className="text-faint" />
                Marked as a day off.{" "}
                {remoteLabels ? "Mark a full or half day" : "Check in"} if you do end up working.
              </p>
            )}

            {showModePicker && (
              <p className="text-xs leading-relaxed text-muted">
                Your account is hybrid: whichever you pick locks today&apos;s mode.
              </p>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}

/**
 * "You have leave today" — shown before the press, not after it.
 *
 * Working a day you had booked off is allowed: the attendance is recorded and
 * the day shows as worked. What must not happen is that it goes unnoticed, since
 * the leave would otherwise stay spent against a balance the person did not use.
 * So the notice carries the fix with it rather than sending them to another page
 * to find it.
 *
 * Withdrawal is offered for THIS DAY only. A five-day leave that somebody worked
 * one day of should lose that one day, and taking the whole thing back because
 * they came in on the Tuesday would be a considerably worse outcome than leaving
 * it alone.
 *
 * Pending leave gets the notice but no withdraw button: there is nothing granted
 * to hand back, and the request can simply be edited or deleted from the leave
 * page while it is still pending.
 */
function LeaveClashNotice({
  leave,
  workDate,
  hasAttendance,
  busy,
  onWithdraw,
}: {
  leave: LeaveDay;
  workDate: string | null;
  hasAttendance: boolean;
  busy: boolean;
  onWithdraw: () => void;
}) {
  const approved = leave.status === "approved";
  const dayLabel = formatDate(workDate ?? leave.date, "d MMM") ?? "today";

  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-lg border px-3 py-2.5",
        approved ? "border-accent-line bg-accent-wash/60" : "border-line bg-surface-sunken/60",
      )}
    >
      <Icon
        name="leave"
        size={16}
        className={cn("mt-0.5 shrink-0", approved ? "text-accent" : "text-faint")}
      />
      <div className="min-w-0 flex-1">
        <p className="text-xs leading-relaxed text-ink-soft">
          {approved ? (
            <>
              You have <span className="font-medium text-ink">approved {leaveLabel(leave.leave_type)}</span>{" "}
              for {dayLabel}.{" "}
              {hasAttendance
                ? "Today is recorded as worked, so this day is still being charged against your leave balance."
                : "You can still record attendance if you're working — the day will show as worked."}
            </>
          ) : (
            <>
              You have a <span className="font-medium text-ink">pending {leaveLabel(leave.leave_type)}</span>{" "}
              request covering {dayLabel}. Recording attendance won't cancel it — edit or delete the
              request from your leave page if you no longer need it.
            </>
          )}
        </p>

        {approved && (
          <button
            type="button"
            disabled={busy}
            onClick={onWithdraw}
            className="mt-1.5 text-[13px] font-medium text-accent underline-offset-4 hover:underline disabled:opacity-50"
          >
            Withdraw {dayLabel} from this leave
          </button>
        )}
      </div>
    </div>
  );
}

/** Only remote-capable accounts can declare a day off. */
function canDeclareDayOff(workMode: string | undefined, lockedMode: string | null) {
  return workMode === "remote" || (workMode === "hybrid" && lockedMode !== "onsite");
}

/**
 * Hybrid's one real fork, before the day has a mode. It sets what the single
 * button will do rather than acting on its own, so pressing it records nothing.
 */
function ModePicker({
  value,
  onChange,
  disabled,
}: {
  value: "onsite" | "remote";
  onChange: (mode: "onsite" | "remote") => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Today's mode"
      className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface-sunken/60 p-1"
    >
      {(["onsite", "remote"] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          role="radio"
          aria-checked={value === mode}
          disabled={disabled}
          onClick={() => onChange(mode)}
          className={cn(
            "rounded-md px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50",
            value === mode ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink",
          )}
        >
          {mode === "onsite" ? "Onsite" : "Remote"}
        </button>
      ))}
    </div>
  );
}

/**
 * The day's sessions, in order. Shown rather than summarised because a total on
 * its own can't be checked: seeing 09:10-13:00 and 14:00-18:30 is what lets
 * someone notice a session they forgot to close, or one that isn't theirs.
 */
function SessionList({ sessions, now }: { sessions: AttendanceSession[]; now: Date }) {
  return (
    <ul className="divide-y divide-line rounded-lg border border-line">
      {sessions.map((session, index) => (
        <li key={`${session.check_in}-${index}`} className="flex items-center gap-3 px-3 py-2">
          <span className="w-4 shrink-0 text-center text-xs text-faint">{index + 1}</span>
          <span className="font-mono text-[13px] tabular-nums text-ink">
            {formatTime(session.check_in)}
          </span>
          <Icon name="arrow-right" size={16} className="shrink-0 text-faint" />
          {session.check_out ? (
            <span className="font-mono text-[13px] tabular-nums text-ink">
              {formatTime(session.check_out)}
            </span>
          ) : (
            <Badge tone="outline">Running</Badge>
          )}
          {/* An open row shows its own elapsed time rather than a dash, so the
              row that's actually moving is the one with a number on it. */}
          <span
            className={cn(
              "ml-auto font-mono text-[13px] tabular-nums",
              session.check_out ? "text-muted" : "text-ink",
            )}
          >
            {session.check_out
              ? formatMinutes(session.minutes)
              : formatMinutes(Math.floor(elapsedMs(session.check_in, now) / 60_000))}
          </span>
          {(session.in_source === "hr" || session.out_source === "hr") && (
            <Badge tone="outline" icon="edit">
              HR
            </Badge>
          )}
        </li>
      ))}
    </ul>
  );
}
